import React, { useRef, useCallback, forwardRef, useEffect, useMemo, useImperativeHandle } from 'react';
import Plyr from 'plyr-react';
import 'plyr-react/plyr.css';
import styles from './Player.module.scss';
import logger from '../../utils/logger';

const extractVideoId = (url) => {
  const regex =
    /(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const matches = url.match(regex);
  return matches ? matches[1] : null;
};

const findScrollableAncestor = (el) => {
  let node = el?.parentElement || null;
  while (node && node !== document.body && node !== document.documentElement) {
    const cs = getComputedStyle(node);
    const oy = cs.overflowY;
    if (
      (oy === 'auto' || oy === 'scroll' || oy === 'overlay') &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return document.scrollingElement || document.documentElement;
};

const isPlyrDomAttached = (plyr) => {
  const container = plyr?.elements?.container;
  return Boolean(container && document.body.contains(container));
};

const isBenignPlyrTeardownError = (error) => {
  const msg = String(error?.message || '');
  return (
    msg.includes("reading 'src'") ||
    msg.includes('not attached to the DOM')
  );
};

const Player = forwardRef(({ Url, onEnded, onPlayStateChange }, ref) => {
  const playerRef = useRef(null);
  const mountedRef = useRef(true);

  const safePlyr = useCallback((action) => {
    if (!mountedRef.current) return;
    const plyr = playerRef.current?.plyr;
    if (!plyr || !isPlyrDomAttached(plyr)) return;
    try {
      action(plyr);
    } catch (error) {
      if (!isBenignPlyrTeardownError(error)) {
        logger.debug('[Player] Plyr action failed:', error);
      }
    }
  }, []);

  useImperativeHandle(ref, () => ({
    play: () => safePlyr((plyr) => plyr.play()),
    pause: () => safePlyr((plyr) => plyr.pause()),
    isPlaying: () => playerRef.current?.plyr?.playing ?? false,
    getProgress: () => {
      const plyr = playerRef.current?.plyr;
      return plyr?.duration ? plyr.currentTime / plyr.duration : 0;
    },
  }), [safePlyr]);
  const wrapperRef = useRef(null);
  const videoId = extractVideoId(Url);
  const hasEndedRef = useRef(false);

  const handleVideoEnd = useCallback(() => {
    if (!mountedRef.current || hasEndedRef.current || !onEnded) return;
    logger.debug('[Player] Video ended, triggering callback');
    hasEndedRef.current = true;
    onEnded();
  }, [onEnded]);

  const handleReady = useCallback(() => {
    // Plyr's autoplay:true races the YouTube iframe onReady hook and logs
    // "player is not attached to the DOM". Start playback only once ready.
    safePlyr((plyr) => plyr.play());
  }, [safePlyr]);

  useEffect(() => {
    // plyr-react's <Plyr> only special-cases `source`/`options`; an `events`
    // prop is spread onto the underlying <video> DOM node, not wired to the
    // real Plyr instance's `.on()` emitter. We attach listeners directly on
    // the instance instead. The instance also isn't available on the ref
    // synchronously on mount (react-aptor exposes it only after an internal
    // state update re-renders), so retry until it appears.
    let cancelled = false;
    let attachedPlyr = null;
    let retryTimeout;

    const onReady = () => handleReady();
    const onEnded = () => handleVideoEnd();
    const onPlaying = () => {
      if (mountedRef.current) onPlayStateChange?.(true);
    };
    const onPause = () => {
      if (mountedRef.current) onPlayStateChange?.(false);
    };

    const tryAttach = () => {
      if (cancelled) return;
      const plyr = playerRef.current?.plyr;
      if (!plyr || typeof plyr.on !== 'function') {
        retryTimeout = window.setTimeout(tryAttach, 50);
        return;
      }
      attachedPlyr = plyr;
      plyr.on('ready', onReady);
      plyr.on('ended', onEnded);
      plyr.on('playing', onPlaying);
      plyr.on('pause', onPause);
    };

    tryAttach();

    return () => {
      cancelled = true;
      window.clearTimeout(retryTimeout);
      if (attachedPlyr && typeof attachedPlyr.off === 'function') {
        attachedPlyr.off('ready', onReady);
        attachedPlyr.off('ended', onEnded);
        attachedPlyr.off('playing', onPlaying);
        attachedPlyr.off('pause', onPause);
      }
    };
  }, [videoId, handleReady, handleVideoEnd, onPlayStateChange]);

  useEffect(() => {
    mountedRef.current = true;
    hasEndedRef.current = false;
    let checkInterval;

    const checkVideoProgress = () => {
      if (!mountedRef.current) return;
      safePlyr((player) => {
        const currentTime = player.currentTime;
        const duration = player.duration;

        if (duration && currentTime >= duration - 0.5 && !hasEndedRef.current) {
          handleVideoEnd();
        }
      });
    };

    checkInterval = setInterval(checkVideoProgress, 1000);

    return () => {
      if (checkInterval) {
        clearInterval(checkInterval);
      }
      // Pause via safePlyr (reads the live ref) so we don't trip the
      // exhaustive-deps warning about playerRef.current in cleanup.
      safePlyr((plyr) => plyr.pause());
      mountedRef.current = false;
    };
  }, [handleVideoEnd, Url, safePlyr]);

  /*
    Wheel-over-player has two distinct ways to eat the page scroll:

    1. The cross-origin YouTube iframe. Chrome routes wheel events over
       cross-origin iframes to the iframe's compositor regardless of
       `pointer-events: none` on the iframe (crbug.com/1068957), so a
       CSS-only fix isn't reliable. We inject a same-origin "wheel
       shield" div as a sibling of the iframe — a real DOM element at
       z-index: 1 becomes the hit target for wheel events.

    2. Plyr's own controls bar (play/progress/volume/settings/fullscreen).
       Plyr attaches wheel handlers to its volume slider + progress
       bar for scrubbing, and any wheel that lands on the controls row
       just gets swallowed because the controls sit above the iframe
       at a higher z-index than the shield.

    Both are fixed by the same `onWheel` — preventDefault + drive the
    nearest scrollable ancestor via `scrollBy`. The shield handles
    case (1) (Chrome iframe hit-test); a capture-phase listener on the
    outer wrapper handles case (2) (controls row + anywhere else inside
    the player). Capture-phase runs before Plyr's own handlers so
    volume/progress never see the wheel, and we only preventDefault —
    click/drag/keyboard on the controls stay fully functional.
  */
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;

    let shield = null;
    let scheduledInstall = 0;
    let mounted = true;

    const onWheel = (e) => {
      const scroller = findScrollableAncestor(wrapper);
      if (!scroller) return;
      e.preventDefault();
      scroller.scrollBy({
        top: e.deltaY,
        left: e.deltaX,
        behavior: 'auto',
      });
    };

    wrapper.addEventListener('wheel', onWheel, {
      passive: false,
      capture: true,
    });

    const install = () => {
      if (!mounted || shield) return;
      const videoHost = wrapper.querySelector(
        '.plyr__video-embed, .plyr__video-wrapper'
      );
      const iframe = videoHost?.querySelector('iframe');
      if (!videoHost || !iframe) return;
      if (getComputedStyle(videoHost).position === 'static') {
        videoHost.style.position = 'relative';
      }
      shield = document.createElement('div');
      shield.className = 'gs-player-wheel-shield';
      shield.setAttribute('aria-hidden', 'true');
      shield.addEventListener('wheel', onWheel, { passive: false });
      videoHost.appendChild(shield);
    };

    const tick = () => {
      install();
      if (!shield && mounted) {
        scheduledInstall = window.setTimeout(tick, 120);
      }
    };
    tick();

    return () => {
      mounted = false;
      window.clearTimeout(scheduledInstall);
      wrapper.removeEventListener('wheel', onWheel, { capture: true });
      if (shield) {
        shield.removeEventListener('wheel', onWheel);
        shield.remove();
        shield = null;
      }
    };
  }, [videoId]);

  return (
    <div ref={wrapperRef} className={styles.player}>
      <Plyr
        ref={playerRef}
        source={useMemo(
          () => ({
            type: 'video',
            sources: [{ src: videoId, provider: 'youtube' }],
          }),
          [videoId]
        )}
        options={useMemo(
          () => {
            // Plyr's default fetches noembed.com for title + dimensions. On
            // mobile that XHR often fails (blocked, flaky network, etc.). Plyr's
            // bundled fetch helper *throws* on XHR `error` with `status === 0`
            // instead of rejecting the Promise, which surfaces as unhandled
            // `Error: 0` in Sentry. We already show the song title in the page
            // chrome — skip the third-party oEmbed call with a tiny data URL
            // that satisfies the same JSON shape Plyr expects.
            const oembedShape = JSON.stringify({
              title: '',
              width: 1280,
              height: 720,
            });
            const youtubeOembedDataUrl = `data:application/json;charset=utf-8,${encodeURIComponent(oembedShape)}`;

            return {
              autoplay: false,
              muted: true,
              // Plyr's own keyboard shortcuts aren't exposed anywhere in our UI
              // (no captions button, skip/seek are custom SongPage controls), and
              // the 'c' shortcut can crash Plyr itself: toggleCaptions() force-picks
              // a caption track even when the video has none, so Plyr dereferences
              // `undefined.language` inside its own listener where we can't catch it.
              keyboard: { focused: false, global: false },
              controls: [
                'play',
                'progress',
                'current-time',
                'mute',
                'volume',
                'settings',
                'fullscreen',
              ],
              urls: {
                youtube: {
                  api: youtubeOembedDataUrl,
                },
              },
              youtube: {
                noCookie: true,
                rel: 0,
                showinfo: 0,
                iv_load_policy: 3,
                modestbranding: 1,
                playsinline: 1,
                controls: 0,
                origin: window.location.origin,
                enablejsapi: 1,
              },
            };
          },
          []
        )}
      />
    </div>
  );
});

export default React.memo(Player);
